import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { motion } from "framer-motion";
import { Lock, AlertCircle, CheckCircle } from "lucide-react";
import { useState, useCallback, useEffect } from "react";
import toast from "react-hot-toast";
import { z } from "zod";

import { FormField, Button } from "@/components/ui";
// import { resetPasswordService } from "@/services/auth";
import {
  colors,
  fontSize,
  fontWeight,
  textColor,
  bgColor,
  borderColor,
} from "@/theme";
import { resetPasswordService, verifyResetTokenService } from "@/services/auth";

const resetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/,
        "Password must contain uppercase, lowercase, number and special character"
      )
  })

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

export default function ResetPassword() {
  const navigate = useNavigate();
  const { token } = useParams();
  const [loading, setLoading] = useState(false);
  const [tokenLoading, setTokenLoading] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);
  const [generalError, setGeneralError] = useState("");

  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    mode: "onSubmit",
    defaultValues: {
      password: "",
    },
  });

  useEffect(() => {
    const verifyToken = async () => {
      try {
        const res =
          await verifyResetTokenService(
            token as string
          );
          if(res.status === 404){
          setGeneralError(
            "This password reset link is invalid or has expired."
          );
          setIsValidToken(false);
          return;
        }
        const response = res.data as any;
        const status =
          response?.Status;

        const message =
          response?.Message;

        if (status === true) {
          setIsValidToken(true);
        } else {
          setGeneralError(
            Array.isArray(message)
              ? message[0]
              : message ||
              "Invalid reset link"
          );
        }
      } catch (error: any) {
        console.log(error);

        if (error?.response?.status === 404) {
          setGeneralError(
            "This password reset link is invalid or has expired."
          );
          setIsValidToken(false);
          return;
        }

        if (error?.response?.status === 400) {
          setGeneralError(
            "Invalid reset token."
          );
          setIsValidToken(false);
          return;
        }

        setGeneralError(
          error?.response?.data?.Message ||
          "Unable to verify reset link."
        );

        setIsValidToken(false);
      } finally {
        setTokenLoading(false);
      }
    };

    if (token) {
      verifyToken();
    } else {
      setGeneralError("Token not found");
      setTokenLoading(false);
    }
  }, [token]);

  const handleApiErrors = useCallback(
    (response: any) => {
      clearErrors();
      setGeneralError("");

      const data =
        response?.errors ||
        response?.data?.errors ||
        response?.data?.error ||
        {};

      if (!data || Object.keys(data).length === 0) {
        setGeneralError(
          response?.message ||
          response?.error ||
          "Unable to reset password"
        );
        return;
      }

      Object.entries(data).forEach(
        ([key, messages]: [string, any]) => {
          const errorMessage = Array.isArray(messages)
            ? messages[0]
            : messages;

          if (
            key === "password"
          ) {
            setError(key as any, {
              type: "server",
              message: errorMessage,
            });
          }
        }
      );

      if (data.non_field_errors) {
        const message = Array.isArray(data.non_field_errors)
          ? data.non_field_errors[0]
          : data.non_field_errors;

        setGeneralError(message);
      }
    },
    [clearErrors, setError]
  );

  const onSubmit = async (
    values: ResetPasswordFormData
  ) => {
    setLoading(true);
    setGeneralError("");

    try {
      const res =
        await resetPasswordService(
          token as string,
          values.password
        );
      const response = res.data as any;

      if (response?.Status === false) {
        const message = Array.isArray(response?.Message)
          ? response.Message[0]
          : response?.Message;

        setGeneralError(message);
        toast.error(message);
        return;
      }

      if (response?.Status === true) {
        toast.success(
          Array.isArray(response?.Message)
            ? response.Message[0]
            : response?.Message ||
            "Password reset successfully"
        );

        setTimeout(() => {
          navigate("/login");
        }, 1500);

        return;
      }
      handleApiErrors(response);
    } catch (err: any) {
      console.error("Reset password error:", err);

      if (!err.response || err.response.status === 0) {
        setGeneralError(
          "Network error. Please check your connection."
        );
        toast.error(
          "Network error. Please check your connection."
        );
        return;
      }

      if (err.response?.status >= 400) {
        handleApiErrors(err.response);
        return;
      }

      const errorMsg =
        err.message ||
        "Unable to reset password. Please try again.";

      setGeneralError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (tokenLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-10 w-10 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p>Verifying reset link...</p>
        </div>
      </div>
    );
  }

  if (!isValidToken) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-lg text-center max-w-md">
          <h2 className="text-2xl font-bold text-red-600 mb-3">
            Invalid Link
          </h2>

          <p className="text-gray-600">
            {generalError}
          </p>

          <button
            onClick={() => navigate("/login")}
            className="mt-5 px-4 py-2 bg-orange-500 text-white rounded-lg"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-gradient-to-br from-orange-50 to-yellow-50">
      {/* LEFT PANEL */}
      <div
        className={`hidden lg:flex flex-col justify-center px-20 py-20 ${textColor.white}`}
        style={{ background: colors.brand.gradient }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="mb-8">
            <p
              className={`${fontSize.sm} ${fontWeight.semibold} tracking-widest mb-2 uppercase`}
            >
              MERA
            </p>

            <h1
              className={`${fontSize["5xl"]} ${fontWeight.bold} mb-2`}
            >
              LUNCH BOX
            </h1>

            <p
              className={`${fontSize.xl} italic text-white/90`}
            >
              Create your new password securely
            </p>
          </div>

          <ul className="space-y-4 text-white/90">
            {[
              "Password must contain uppercase and lowercase letters",
              "Include at least one number",
              "Include at least one special character",
              "Minimum length of 8 characters",
            ].map((item) => (
              <li
                key={item}
                className="flex items-center gap-3"
              >
                <CheckCircle className="w-5 h-5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>

      {/* RIGHT PANEL */}
      <div className="flex items-center justify-center px-4 py-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md space-y-8"
        >
          {/* LOGO */}
          <div className="flex justify-center">
            <div
              className={`w-32 h-32 rounded-full overflow-hidden border-4 ${borderColor.orange} shadow-2xl`}
            >
              <img
                src="/mlblogo.jpeg"
                alt="Logo"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* TITLE */}
          <div className="text-center">
            <h2
              className={`${fontSize["4xl"]} ${fontWeight.bold} ${textColor.heading}`}
            >
              Reset Password
            </h2>

            <p
              className={`${fontSize.lg} ${textColor.muted}`}
            >
              Enter your new password below
            </p>
          </div>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-6"
          >
            <FormField
              control={control}
              name="password"
              label="Password"
              placeholder="Enter new password"
              type="password"
              icon={
                <Lock
                  className={`w-5 h-5 ${textColor.icon}`}
                />
              }
              disabled={loading}
              error={errors.password?.message}
              onClear={() => {
                clearErrors("password");
                setGeneralError("");
              }}
            />

            {generalError && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{
                  opacity: 1,
                  height: "auto",
                }}
                className={`p-3 ${bgColor.dangerLight} border ${borderColor.danger} rounded-lg`}
              >
                <div
                  className={`flex items-start gap-2 ${fontSize.sm} ${textColor.dangerSoft}`}
                >
                  <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>{generalError}</span>
                </div>
              </motion.div>
            )}

            <Button
              type="submit"
              disabled={loading || isSubmitting}
              size="lg"
              className="w-full"
              style={{
                background: colors.brand.gradient,
              }}
            >
              {loading || isSubmitting ? (
                <>
                  <svg
                    className="w-5 h-5 mr-2 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.37 0 0 5.37 0 12h4z"
                    />
                  </svg>
                  Resetting...
                </>
              ) : (
                "Reset Password"
              )}
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}